import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { LinkIcon, XIcon } from 'lucide-react'
import { updatePhoneCallLog } from '../../api/phoneCallLogs'
import { supabase } from '../../lib/supabase'
import {
  INBOX_ENTITY_LINK_FIELDS,
  fetchInboxEntityLinkLabels,
  type InboxEntityLinkKey,
} from '../../lib/inboxEntityLinks'
import { InboxEntityLinkPopover } from './InboxEntityLinkPopover'

interface InboxEntityLinkToolbarProps {
  itemType: 'conversation' | 'phone_call_log'
  itemId: string
  links: Partial<Record<InboxEntityLinkKey, string | null | undefined>>
  onUpdated: () => void
  onCustomerLinked?: (customerId: string) => void
  compact?: boolean
  iconOnly?: boolean
  pickerAsModal?: boolean
}

/** Link / unlink CRM records on a conversation or call log. */
export function InboxEntityLinkToolbar({
  itemType,
  itemId,
  links,
  onUpdated,
  onCustomerLinked,
  compact = false,
  iconOnly = false,
  pickerAsModal = false,
}: InboxEntityLinkToolbarProps) {
  const navigate = useNavigate()
  const [pickerOpen, setPickerOpen] = useState(false)
  const [linkedLabels, setLinkedLabels] = useState<Record<string, string>>({})

  useEffect(() => {
    fetchInboxEntityLinkLabels(links).then(setLinkedLabels)
  }, [links, itemId])

  async function linkTo(entityKey: InboxEntityLinkKey, entityId: string, label: string) {
    try {
      if (itemType === 'phone_call_log') {
        await updatePhoneCallLog(itemId, { [entityKey]: entityId } as any)
      } else {
        await supabase.from('conversations').update({ [entityKey]: entityId }).eq('id', itemId)
      }
      setLinkedLabels((prev) => ({ ...prev, [entityKey]: label }))
      setPickerOpen(false)
      if (entityKey === 'customer_id') onCustomerLinked?.(entityId)
      onUpdated()
    } catch {
      alert('Failed to link record')
    }
  }

  async function unlink(key: InboxEntityLinkKey) {
    try {
      if (itemType === 'phone_call_log') {
        await updatePhoneCallLog(itemId, { [key]: null } as any)
      } else {
        await supabase.from('conversations').update({ [key]: null }).eq('id', itemId)
      }
      setLinkedLabels((prev) => {
        const next = { ...prev }
        delete next[key]
        return next
      })
      onUpdated()
    } catch {
      alert('Failed to unlink record')
    }
  }

  const activeLinks = INBOX_ENTITY_LINK_FIELDS.filter((f) => links[f.key])

  const picker = pickerOpen ? (
    pickerAsModal ? (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
        <div className="relative">
          <InboxEntityLinkPopover onSelect={linkTo} onClose={() => setPickerOpen(false)} />
        </div>
      </div>
    ) : (
      <InboxEntityLinkPopover
        onSelect={linkTo}
        onClose={() => setPickerOpen(false)}
        className={compact ? 'absolute left-0 top-full mt-1 z-20' : 'absolute right-0 top-12 z-20'}
      />
    )
  ) : null

  if (iconOnly) {
    return (
      <>
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          title="Link to CRM record"
          className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700"
        >
          <LinkIcon className="w-4 h-4" />
        </button>
        {picker}
      </>
    )
  }

  return (
    <div className={`relative ${compact ? '' : 'space-y-2'}`}>
      <div className="flex items-center gap-2 flex-wrap">
        {activeLinks.map((f) => (
          <span
            key={f.key}
            className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 bg-gray-100 text-gray-700 text-[11px] rounded-full"
          >
            <button type="button" onClick={() => navigate(f.route(links[f.key]!))} className="hover:underline">
              {f.label}: {linkedLabels[f.key] || '...'}
            </button>
            <button type="button" onClick={() => unlink(f.key)} className="text-gray-400 hover:text-red-600">
              <XIcon className="w-2.5 h-2.5" />
            </button>
          </span>
        ))}
        <button
          type="button"
          onClick={() => setPickerOpen((v) => !v)}
          title="Link to customer, lead, property, job, or other CRM record"
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
        >
          <LinkIcon className="w-3.5 h-3.5" />
          Link record
        </button>
      </div>
      {picker}
    </div>
  )
}
